/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-11-16 15:58:53
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Card, DataTable, SkeletonDisplayText, Pagination, Icon, Tooltip } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { CaretDownMinor, CaretUpMinor } from '@shopify/polaris-icons';
import Filter from 'src/components/containers/analytics-v2/filter';
import PopoverActionButton from 'src/components/views/analytics-v2/popover-action-button';
import { getUTCDate } from 'src/helpers/date-time-helper';
import { formatNumberByLocale } from 'src/helpers';
import { QUERY_EXECUTION_KEY, SEARCH_ANALYTIC_METRIC, FEATURES } from 'src/constants/analytics';
import * as AnalyticsActions from 'src/stores/actions/analytics-v2-actions';
import { buildQueryKey, findExecutionId } from 'src/helpers/analytics-query-execution-id-helper';
import * as AnalyticsQueryExecutionIdActions from 'src/stores/actions/analytics-query-execution-id-actions';
import { onAbortApiAnalytic } from 'src/helpers/analytics-v2-helpers';

type Props = {
  performActionSurvey: () => void
};
type ComposedProps = Props & WithI18nProps;
type State = {
  perPage: number,
  page: number,
  hasNext: boolean,
  hasPrevious: boolean,
  rows: any[],
  directionSort: 'descending' | 'ascending'
};

const fromDateStateName = 'searchAnalyticsFromDate';
const toDateStateName = 'searchAnalyticsToDate';
const compareStateName = 'searchAnalyticsCompare';
const DESCENDING = 'descending';

class SearchAnalyticsSearchesWithoutClicks extends Component<ComposedProps, State> {
  state = {
    perPage: 10,
    page: 0,
    hasPrevious: false,
    hasNext: false,
    rows: [],
    directionSort: DESCENDING
  };

  componentDidMount() {
    const { fromDate, toDate } = this.props;
    if (fromDate && toDate) {
      const fromDateUTC = getUTCDate(fromDate, 0, 0, 0);
      const toDateUTC = getUTCDate(toDate, 24, 0, 0, 0);

      this.getTopSearchTermsWithoutClick(fromDateUTC, toDateUTC);
    }
  }

  componentWillReceiveProps(nextProps) {
    const { loading, fromDate, toDate } = this.props;
    const { perPage } = this.state;
    const {
      loading: nextLoading,
      success: nextSuccess,
      fromDate: nextFromDate,
      toDate: nextToDate
    } = nextProps;

    // Check new data from API
    if (loading && !nextLoading && nextSuccess) {
      let { rows } = nextProps;
      const hasNext = _.size(rows) > perPage;
      this.processTopSearchTermsWithoutClickExecutionId(this.props, nextProps);
      return this.setState({ rows, hasNext });
    }

    if (fromDate !== nextFromDate || toDate !== nextToDate) {
      const from = new Date(nextFromDate);
      const to = new Date(nextToDate);
      const fromDateUTC = getUTCDate(from);
      const toDateUTC = getUTCDate(to, 24);

      onAbortApiAnalytic();
      setTimeout(() => {
        this.getTopSearchTermsWithoutClick(fromDateUTC, toDateUTC);
      }, 500);
    }
  }

  processTopSearchTermsWithoutClickExecutionId = (props, nextProps) => {
    const { executionId } = props;

    const { fromDate: nextFromDate, toDate: nextToDate, executionId: nextExecutionId } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: false
    });

    // Update execution id
    if (nextExecutionId && executionId !== nextExecutionId) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.TOP_SEARCH_TERMS_WITHOUT_CLICK,
        executionId: nextExecutionId
      });
    }
  };

  getTopSearchTermsWithoutClick = (fromDate, toDate) => {
    const { executionIds, i18n } = this.props;

    const compare = false;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    const queryExecutionKey = QUERY_EXECUTION_KEY.TOP_SEARCH_TERMS_WITHOUT_CLICK;
    // Get execution id if existing
    const queryExecutionId = findExecutionId(queryKey, queryExecutionKey, executionIds);

    this.props.getTopSearchTermsWithoutClick(fromDate, toDate, queryExecutionId, i18n);
  };

  buildRows = () => {
    const { rows, page, perPage } = this.state;
    const { tenantApp } = this.props;
    return _(rows)
      .slice(page * perPage, page * perPage + perPage)
      .map((searchEvent, index) => {
        const { searchTerm, totalSearchesWithoutClicks, noClickRate } = searchEvent;
        return [
          <div>{searchTerm}</div>,
          <div>{formatNumberByLocale(totalSearchesWithoutClicks)}</div>,
          <div>{noClickRate}%</div>,
          <PopoverActionButton searchTerm={searchTerm} tenantApp={tenantApp} />
        ];
      })
      .value();
  };

  renderSkeleton = (numberOfRow: number, numberOfColumn: number) => {
    return _.range(numberOfRow).map(__ =>
      _.range(numberOfColumn).map(__ => <SkeletonDisplayText size="small" />)
    );
  };

  handleSort = (index, direction) => {
    const { rows } = this.state;
    const MapField = {
      0: 'searchTerm',
      1: 'totalSearches',
      2: 'noClickRate'
    };
    const _shortDirection = direction === DESCENDING ? 'desc' : 'asc';
    this.setState({
      directionSort: direction,
      rows: _.orderBy(rows, MapField[index], _shortDirection)
    });
  };

  nextPage = () => {
    const { page, perPage, rows } = this.state;
    const next = page + 1;
    this.setState({
      page: next,
      hasNext: _.size(rows) > next * perPage + perPage,
      hasPrevious: next > 0
    });
  };

  previousPage = () => {
    const { page, perPage, rows } = this.state;
    const previous = page - 1;
    this.setState({
      page: previous,
      hasPrevious: previous > 0,
      hasNext: previous * perPage < _.size(rows)
    });
  };

  renderCaretIcon = () => {
    const direction = _.get(this.state, 'directionSort', DESCENDING);
    return (
      <span className="Polaris-DataTable__Icon--custom">
        <Icon source={direction === DESCENDING ? CaretDownMinor : CaretUpMinor} color="base" />
      </span>
    );
  };

  renderPaginationLabel = () => {
    const { page, perPage, rows } = this.state;
    const totalPage = Math.ceil(_.size(rows) / perPage);
    if (_.isEmpty(rows)) return `${page}/${totalPage}`;
    return `${page + 1}/${totalPage}`;
  };

  render() {
    const { i18n, openReviewAddonModal, performActionSurvey, loading, success, rows } = this.props;
    const { hasNext, hasPrevious } = this.state;

    let visibleRows = this.renderSkeleton(4, 4);
    if (!loading && success) {
      visibleRows = this.buildRows();
    }

    return (
      <div className="BC-Page BC-Page__SearchAnalytics__SearchesWithoutClicks">
        <Filter
          feature={FEATURES.SEARCH}
          performActionSurvey={performActionSurvey}
          fromDateStateName={fromDateStateName}
          toDateStateName={toDateStateName}
          compareStateName={compareStateName}
          metric={SEARCH_ANALYTIC_METRIC.SEARCH_WITHOUT_CLICK}
          showCompare={false}
          openReviewAddonModal={openReviewAddonModal}
        />
        <div>
          <Card
            title={
              <Tooltip
                preferredPosition="above"
                content={i18n.translate(
                  'AdminHTML.Analytics.Common.tooltip_TableSearchesWithoutClick'
                )}>
                <span className="Polaris-Heading">
                  {i18n.translate(
                    'AdminHTML.Analytics.SearchAnalytics.searchesWithoutClicks_cardTitle'
                  )}
                </span>
              </Tooltip>
            }
            sectioned>
            <DataTable
              columnContentTypes={['text', 'numeric', 'numeric', 'numeric']}
              sortable={[!loading, !loading, !loading, false]}
              onSort={this.handleSort}
              defaultSortDirection="descending"
              initialSortColumnIndex={1}
              headings={[
                <div className="header-with-sortable">
                  {i18n.translate(
                    'AdminHTML.Analytics.SearchAnalytics.searchesWithoutClicks_searchTermHeader'
                  )}
                  {this.renderCaretIcon()}
                </div>,
                <div className="header-with-sortable">
                  {i18n.translate('AdminHTML.Analytics.Common.totalSearches_without_clicks')}
                  {this.renderCaretIcon()}
                </div>,
                <div className="header-with-sortable">
                  {i18n.translate(
                    'AdminHTML.Analytics.SearchAnalytics.searchesWithoutClicks_noClickRateHeader'
                  )}
                  {this.renderCaretIcon()}
                </div>,
                i18n.translate(
                  'AdminHTML.Analytics.SearchAnalytics.searchesWithoutClicks_actionHeader'
                )
              ]}
              rows={visibleRows}
            />
          </Card>
          {!_.isEmpty(rows) && !loading && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
              <Pagination
                label={this.renderPaginationLabel()}
                hasPrevious={hasPrevious}
                onPrevious={this.previousPage}
                hasNext={hasNext}
                onNext={this.nextPage}
              />
            </div>
          )}
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  changeValue: (name, value) => dispatch(AnalyticsActions.changeValue(name, value)),
  getTopSearchTermsWithoutClick: (from, to, queryExecutionId, i18n) =>
    dispatch(AnalyticsActions.getTopSearchTermsWithoutClick(from, to, queryExecutionId, i18n)),
  updateExecutionId: data => dispatch(AnalyticsQueryExecutionIdActions.updateExecutionId(data))
});

const mapStateToProps = (state, props) => {
  return {
    tenantApp: _.get(state, 'oauth.tenantApp', {}),
    executionIds: _.get(state.analyticsQueryExecutionId, 'list', []),
    fromDate: _.get(state.analyticsV2, fromDateStateName),
    toDate: _.get(state.analyticsV2, toDateStateName),
    compare: _.get(state.analyticsV2, compareStateName, false),
    loading: _.get(state.analyticsV2, 'topSearchTermsWithoutClickLoading', false),
    success: _.get(state.analyticsV2, 'topSearchTermsWithoutClickSuccess', false),
    rows: _.get(state.analyticsV2, 'topSearchTermsWithoutClick', []),
    executionId: _.get(state.analyticsV2, 'topSearchTermsWithoutClickExecutionId', [])
  };
};

export default withI18n()(
  connect(mapStateToProps, mapDispatchToProps)(SearchAnalyticsSearchesWithoutClicks)
);
